import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ListOrdered, Plus, Rows3, Upload, X } from 'lucide-react';
import { Heading } from '../layout';
import { useUIColors } from '../../hooks/useTheme';
import PurchaseOrdersTable from '../purchaseOrders/PurchaseOrdersTable';
import PurchaseOrderItemsTable from '../purchaseOrders/PurchaseOrderItemsTable';
import { PurchaseOrdersForm } from '../forms/PurchaseOrdersForm';
import { SuppliersForm } from '../forms/SuppliersForm';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import PurchaseOrderInvoicingModal from './PurchaseOrderInvoicingModal';
import PurchaseOrdersBulkImportModal from './PurchaseOrdersBulkImportModal';
import PurchaseOrderItemsBulkImportModal from './PurchaseOrderItemsBulkImportModal';
import PeriodSummaryTable from './PeriodSummaryTable';
import MonthlyDistributionChart from './MonthlyDistributionChart';
import purchaseOrdersAPI from '../../services/purchaseOrdersAPI';
import inventoryAPI from '../../services/inventoryAPI';
import purchaseRequestsAPI from '../../services/purchaseRequestsAPI';
import suppliersAPI from '../../services/suppliersAPI';
import supplierContactsAPI from '../../services/supplierContactsAPI';
import procurementCatalogsAPI from '../../services/procurementCatalogsAPI';
import usersAPI from '../../services/usersAPI';
import { getActiveOptionsFromRows, toUiCatalogRow } from './procurementCatalogDefaults';
import { capitalizeWords, formatChileanRut, normalizeProperCaseText } from '../../utils/textUtils';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  collectDates,
  getAvailableYearsFromDates,
  getMonthsInRange,
  resolvePeriodSelection,
  isDateInRange,
  isDateInSelectedPeriod,
  parseDateSafe,
  periodSelectStyle,
  periodLabelStyle,
} from './periodSummaryUtils';
import { CHART_METRIC_AMOUNT, CHART_METRIC_COUNT } from './chartFilterUtils';
import {
  PO_SUMMARY_METRIC_OPTIONS,
  PO_STATUS_ORDER,
  PO_STATUS_STACK_DIMENSION,
  PO_ITEM_TYPE_STACK_DIMENSION,
  getPoAnchorDate,
  normalizePoStatus,
  normalizePoItemType,
  poAmountClp,
  poLineAmountClp,
  formatSummaryCount,
  formatSummaryMoney,
  isPendingPoApprovalStatus,
} from './purchaseOrderSummary';
import { downloadPurchaseOrderPdf } from '../../utils/purchaseOrderPdf';

const EMPTY_SUPPLIER_FORM = {
  supplier_code: '',
  name: '',
  business_name: '',
  rut: '',
  address: '',
  supplier_type: '',
  status: 'Activo',
  payment_conditions: [],
  addresses: [],
  bank_accounts: [],
  contacts: [],
};

const syncSupplierContacts = async (supplierId, desiredContacts = []) => {
  const desired = (desiredContacts || []).filter((c) => String(c.full_name || '').trim());
  for (const contact of desired) {
    await supplierContactsAPI.create({
      full_name: capitalizeWords(String(contact.full_name || '').trim()),
      role: capitalizeWords(String(contact.role || '').trim()),
      email: String(contact.email || '').trim(),
      phone: String(contact.phone || '').trim(),
      status: String(contact.status || 'Activo').trim() || 'Activo',
      supplier_id: supplierId,
    });
  }
};

const PO_STATUS_CHART_METRICS = [
  { ...CHART_METRIC_COUNT, label: 'Cantidad de OC', totalLabel: 'órdenes' },
  { ...CHART_METRIC_AMOUNT, label: 'Montos de OC', totalLabel: 'CLP' },
];

const PO_ITEM_TYPE_CHART_METRICS = [
  { ...CHART_METRIC_AMOUNT, label: 'Montos por tipo', totalLabel: 'CLP' },
];

const PO_CHART_AXIS_MODES = [{ key: 'month', label: 'Mes' }];

const todayIso = () => new Date().toISOString().slice(0, 10);

const EMPTY_LINE = {
  id: null,
  item_type: '',
  item_code: '',
  description: '',
  detail: '',
  quantity: 1,
  unit: 'UN',
  format_code: '',
  unit_price: 0,
  purchase_request_id: '',
};

const EMPTY_FORM = {
  po_code: '',
  supplier_id: '',
  supplier_contact_id: '',
  order_date: todayIso(),
  expected_delivery_date: '',
  payment_terms: '',
  currency: 'CLP',
  document_type: '',
  exchange_rate: 1,
  status: 'Borrador',
  delivery_place: '',
  delivery_conditions: '',
  buyer_name: '',
  comments: '',
  notes: '',
  advance_amount: 0,
  withholdings_amount: 0,
  items: [{ ...EMPTY_LINE }],
};

const mapSourceToForm = (source) => ({
  po_code: source.po_code || '',
  supplier_id: source.supplier_id ? String(source.supplier_id) : '',
  supplier_contact_id: source.supplier_contact_id ? String(source.supplier_contact_id) : '',
  order_date: source.order_date ? String(source.order_date).slice(0, 10) : todayIso(),
  expected_delivery_date: source.expected_delivery_date
    ? String(source.expected_delivery_date).slice(0, 10)
    : '',
  payment_terms: source.payment_terms || '',
  currency: source.currency || 'CLP',
  document_type: source.document_type || '',
  exchange_rate: source.exchange_rate ?? 1,
  status: source.status || 'Borrador',
  delivery_place: source.delivery_place || '',
  delivery_conditions: source.delivery_conditions || '',
  buyer_name: source.buyer_name || '',
  comments: source.comments || '',
  notes: source.notes || '',
  advance_amount: source.advance_amount ?? 0,
  withholdings_amount: source.withholdings_amount ?? 0,
  items:
    Array.isArray(source.items) && source.items.length
      ? source.items.map((item) => ({
          id: item.id,
          item_type: item.item_type || '',
          item_code: item.item_code || '',
          description: item.description || '',
          detail: item.detail || '',
          quantity: item.quantity ?? 1,
          unit: item.unit || 'UN',
          format_code: item.format_code || '',
          unit_price: item.unit_price ?? 0,
          purchase_request_id: item.purchase_request_id ? String(item.purchase_request_id) : '',
          purchase_request_code: item.purchase_request_code || '',
        }))
      : [{ ...EMPTY_LINE }],
});

const PaintPurchaseOrdersWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();

  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemsError, setItemsError] = useState('');

  const [suppliers, setSuppliers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [itemTypeOptions, setItemTypeOptions] = useState([]);
  const [deliveryConditionOptions, setDeliveryConditionOptions] = useState([]);
  const [paymentConditionOptions, setPaymentConditionOptions] = useState([]);
  const [supplierTypeOptions, setSupplierTypeOptions] = useState([]);
  const [buyerOptions, setBuyerOptions] = useState([]);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [formatOptions, setFormatOptions] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showBulkImportChooser, setShowBulkImportChooser] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [isImportingOrders, setIsImportingOrders] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [showItemsBulkImportModal, setShowItemsBulkImportModal] = useState(false);
  const [isImportingItems, setIsImportingItems] = useState(false);
  const [itemsBulkImportResult, setItemsBulkImportResult] = useState(null);

  const [showQuickSupplierForm, setShowQuickSupplierForm] = useState(false);
  const [quickSupplierFormData, setQuickSupplierFormData] = useState(EMPTY_SUPPLIER_FORM);
  const [quickSupplierFormError, setQuickSupplierFormError] = useState('');
  const [isSavingQuickSupplier, setIsSavingQuickSupplier] = useState(false);

  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [invoiceSaving, setInvoiceSaving] = useState(false);

  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState('all');
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [chartsCollapsed, setChartsCollapsed] = useState(false);
  const [summaryMetric, setSummaryMetric] = useState('count');
  const [listTab, setListTab] = useState('orders');

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await purchaseOrdersAPI.getAll();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudieron cargar las órdenes de compra.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrderItems = useCallback(async () => {
    setItemsLoading(true);
    setItemsError('');
    try {
      const data = await purchaseOrdersAPI.getAllItems();
      setOrderItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setItemsError(err?.message || 'No se pudieron cargar los ítems de órdenes de compra.');
      setOrderItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [
        suppliersData,
        contactsData,
        catalogs,
        usersData,
        requestsData,
        catalogData,
        poCatalogData,
        formatsData,
      ] = await Promise.all([
        suppliersAPI.getAll(),
        supplierContactsAPI.getAll(),
        procurementCatalogsAPI.getGrouped(),
        usersAPI.getAll().catch((err) => {
          console.error('Error loading users for ejecutivo:', err);
          return [];
        }),
        purchaseRequestsAPI.getAll().catch((err) => {
          console.error('Error loading purchase requests:', err);
          return [];
        }),
        inventoryAPI.getCatalog().catch((err) => {
          console.error('Error loading inventory catalog:', err);
          return [];
        }),
        purchaseOrdersAPI.getCatalog().catch((err) => {
          console.error('Error loading PO item catalog:', err);
          return [];
        }),
        inventoryAPI.getFormats().catch((err) => {
          console.error('Error loading formats:', err);
          return [];
        }),
      ]);
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      setContacts(Array.isArray(contactsData) ? contactsData : []);
      setDocumentTypeOptions(
        getActiveOptionsFromRows((catalogs?.document_types || []).map(toUiCatalogRow))
      );
      setCurrencyOptions(
        getActiveOptionsFromRows((catalogs?.currencies || []).map(toUiCatalogRow), {
          valueFromCode: true,
        })
      );
      setItemTypeOptions(getActiveOptionsFromRows((catalogs?.item_types || []).map(toUiCatalogRow)));
      setDeliveryConditionOptions(
        getActiveOptionsFromRows((catalogs?.delivery_conditions || []).map(toUiCatalogRow))
      );
      setPaymentConditionOptions(
        getActiveOptionsFromRows((catalogs?.payment_conditions || []).map(toUiCatalogRow))
      );
      setSupplierTypeOptions(
        getActiveOptionsFromRows((catalogs?.supplier_types || []).map(toUiCatalogRow))
      );
      const activeUsers = (Array.isArray(usersData) ? usersData : []).filter((user) => {
        const status = String(user?.status || 'Activo').trim().toLowerCase();
        return status === 'activo' || status === 'active' || status === '';
      });
      setBuyerOptions(
        activeUsers.map((user) => {
          const label = user.full_name || user.user_code || `Usuario ${user.id}`;
          return { value: label, label };
        })
      );
      setPurchaseRequests(Array.isArray(requestsData) ? requestsData : []);
      const invCatalog = Array.isArray(catalogData) ? catalogData : [];
      const poCatalog = Array.isArray(poCatalogData) ? poCatalogData : [];
      const mergedCatalog = [];
      const seen = new Set();
      [...invCatalog, ...poCatalog].forEach((row) => {
        const sku = String(row.sku || row.item_code || row.code || '').trim().toUpperCase();
        const name = String(row.description || row.name || '').trim().toLowerCase();
        const type = String(row.item_type || '').trim().toLowerCase();
        const key = sku || `${type}|${name}`;
        if (!key || seen.has(key)) return;
        seen.add(key);
        mergedCatalog.push({
          ...row,
          sku: row.sku || row.item_code || row.code || '',
          code: row.code || row.sku || row.item_code || '',
          description: row.description || row.name || '',
          name: row.name || row.description || '',
          item_type: row.item_type || '',
          unit: row.unit || 'UN',
          format_code: row.format_code || '',
          unit_price: row.unit_price ?? row.unit_cost ?? row.last_unit_price ?? 0,
          unit_cost: row.unit_cost ?? row.unit_price ?? row.last_unit_price ?? 0,
        });
      });
      setCatalogProducts(mergedCatalog);
      setFormatOptions(Array.isArray(formatsData) ? formatsData : []);
    } catch (err) {
      console.error('Error loading PO lookups:', err);
    }
  }, []);

  useEffect(() => {
    loadOrders();
    loadOrderItems();
    loadLookups();
  }, [loadOrders, loadOrderItems, loadLookups]);

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: String(s.id),
        label: `${s.supplier_code || ''} · ${s.name}`.trim(),
      })),
    [suppliers]
  );

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => String(s.id) === String(formData.supplier_id)) || null,
    [suppliers, formData.supplier_id]
  );

  const supplierRut = selectedSupplier?.rut || '';

  const contactOptions = useMemo(() => {
    const supplierId = Number(formData.supplier_id || 0);
    return contacts
      .filter((c) => !supplierId || Number(c.supplier_id) === supplierId)
      .map((c) => ({
        value: String(c.id),
        label: c.full_name || `Contacto ${c.id}`,
      }));
  }, [contacts, formData.supplier_id]);

  const purchaseRequestOptions = useMemo(() => {
    const selectable = purchaseRequests.filter((req) => {
      const status = String(req.status || '').trim().toLowerCase();
      return status !== 'rechazada' && status !== 'borrador';
    });
    const list = selectable.length ? selectable : purchaseRequests;
    return list.map((req) => {
      const code = req.request_code || `SOL-${req.id}`;
      const area = String(req.department || '').trim();
      const requester = String(req.requester_name || '').trim();
      const parts = [code, area, requester].filter(Boolean);
      return { value: String(req.id), label: parts.join(' · ') };
    });
  }, [purchaseRequests]);

  const orderDateValues = useMemo(
    () => collectDates(orders, getPoAnchorDate),
    [orders]
  );

  const availableYears = useMemo(
    () => getAvailableYearsFromDates(orderDateValues),
    [orderDateValues]
  );

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: orderDateValues,
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Resumen OC mensual',
          annual: 'Resumen OC anual',
        },
      }),
    [periodFilter, orderDateValues, secondaryYear, secondaryMonth]
  );

  useEffect(() => {
    if (!availableYears.length) return;
    if (
      periodFilter === 'annual' ||
      periodFilter === 'annual_ytd' ||
      periodFilter === 'quarterly' ||
      periodFilter === 'semestral'
    ) {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly') {
      if (!availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear(String(availableYears[availableYears.length - 1]));
      }
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (
      nextPeriod === 'annual' ||
      nextPeriod === 'annual_ytd' ||
      nextPeriod === 'quarterly' ||
      nextPeriod === 'semestral'
    ) {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(
        String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear)
      );
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const statusChartItems = useMemo(() => {
    const range = selectedPeriod?.range;
    return orders
      .filter((order) => {
        const date = getPoAnchorDate(order);
        if (!date) return false;
        if (range) return isDateInRange(date, range);
        return isDateInSelectedPeriod(date, selectedPeriod);
      })
      .map((order) => ({
        date: getPoAnchorDate(order),
        status: normalizePoStatus(order.status),
        purchaseOrderId: Number(order.id),
        amount: poAmountClp(order),
      }));
  }, [orders, selectedPeriod]);

  const itemTypeChartItems = useMemo(() => {
    const range = selectedPeriod?.range;
    const orderById = new Map(
      (orders || []).map((order) => [Number(order.id), order])
    );
    const linesSumByPo = new Map();
    (orderItems || []).forEach((item) => {
      const poId = Number(item.purchase_order_id);
      if (!poId) return;
      const line = Number(item.line_total || 0);
      if (!Number.isFinite(line) || line <= 0) return;
      linesSumByPo.set(poId, (linesSumByPo.get(poId) || 0) + line);
    });

    return (orderItems || [])
      .filter((item) => {
        const date = item.order_date || '';
        if (!date) return false;
        if (range) return isDateInRange(date, range);
        return isDateInSelectedPeriod(date, selectedPeriod);
      })
      .map((item) => {
        const poId = Number(item.purchase_order_id);
        const order = orderById.get(poId) || null;
        return {
          date: item.order_date,
          itemType: normalizePoItemType(item.item_type),
          purchaseOrderId: poId,
          amount: poLineAmountClp(
            { ...item, po_lines_sum: linesSumByPo.get(poId) || 0 },
            order
          ),
          id: item.id,
        };
      });
  }, [orderItems, orders, selectedPeriod]);

  const chartPeriods = useMemo(() => {
    if (selectedPeriod?.mode === 'year') {
      return selectedPeriod.columns || [];
    }
    const range = selectedPeriod?.range;
    if (range?.start && range?.end) {
      return getMonthsInRange(range.start, range.end);
    }
    return selectedPeriod?.columns || [];
  }, [selectedPeriod]);

  const purchaseOrderSummaryRows = useMemo(() => {
    const periods = selectedPeriod.columns || [];
    const periodStart = periods[0]?.start || selectedPeriod.range?.start || null;

    const inBucket = (order, bucket) => {
      const date = parseDateSafe(getPoAnchorDate(order));
      return Boolean(date && date >= bucket.start && date <= bucket.end);
    };
    const upToBucket = (order, bucket) => {
      const date = parseDateSafe(getPoAnchorDate(order));
      if (!date || date > bucket.end) return false;
      if (periodStart && date < periodStart) return false;
      return true;
    };
    const statusOf = (order) => normalizePoStatus(order.status);

    if (summaryMetric === 'amount') {
      const sumBy = (predicate) =>
        periods.map((bucket) =>
          orders
            .filter((order) => inBucket(order, bucket) && predicate(order))
            .reduce((acc, order) => acc + poAmountClp(order), 0)
        );
      const cumSumBy = (predicate) =>
        periods.map((bucket) =>
          orders
            .filter((order) => upToBucket(order, bucket) && predicate(order))
            .reduce((acc, order) => acc + poAmountClp(order), 0)
        );

      return [
        {
          label: 'Monto total OC (CLP)',
          tone: 'neutral',
          values: sumBy(() => true).map(formatSummaryMoney),
        },
        ...PO_STATUS_ORDER.map((status) => ({
          label: `Monto ${status}`,
          tone:
            status === 'Recepción Conforme' || status === 'Pagado a Tiempo' || status === 'Aprobada'
              ? 'client'
              : status === 'Anulada' || status === 'Recepción No Conforme'
                ? 'prospect'
                : 'neutral',
          values: sumBy((order) => statusOf(order) === status).map(formatSummaryMoney),
        })),
        {
          label: 'Monto total acum. período (CLP)',
          tone: 'total',
          values: cumSumBy(() => true).map(formatSummaryMoney),
        },
        {
          label: 'Monto recepción conforme acum. (CLP)',
          tone: 'total',
          values: cumSumBy((order) => statusOf(order) === 'Recepción Conforme').map(formatSummaryMoney),
        },
      ];
    }

    const countBy = (predicate) =>
      periods.map((bucket) => orders.filter((order) => inBucket(order, bucket) && predicate(order)).length);
    const cumCountBy = (predicate) =>
      periods.map(
        (bucket) => orders.filter((order) => upToBucket(order, bucket) && predicate(order)).length
      );

    return [
      {
        label: 'Nuevas órdenes de compra',
        tone: 'neutral',
        values: countBy(() => true).map(formatSummaryCount),
      },
      ...PO_STATUS_ORDER.map((status) => ({
        label: `OC ${status}`,
        tone:
          status === 'Recepción Conforme' || status === 'Pagado a Tiempo' || status === 'Aprobada'
            ? 'client'
            : status === 'Anulada' || status === 'Recepción No Conforme'
              ? 'prospect'
              : 'neutral',
        values: countBy((order) => statusOf(order) === status).map(formatSummaryCount),
      })),
      {
        label: 'Total OC (acum. período)',
        tone: 'total',
        values: cumCountBy(() => true).map(formatSummaryCount),
      },
      {
        label: 'Total recepción conforme (acum. período)',
        tone: 'total',
        values: cumCountBy((order) => statusOf(order) === 'Recepción Conforme').map(formatSummaryCount),
      },
    ];
  }, [orders, selectedPeriod, summaryMetric]);

  const paymentTermOptions = useMemo(() => {
    const terms = Array.isArray(selectedSupplier?.payment_conditions)
      ? selectedSupplier.payment_conditions
      : [];
    return terms.map((term) => ({ value: term, label: term }));
  }, [selectedSupplier]);

  const assignPaymentTermOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    const push = (value) => {
      const label = String(value || '').trim();
      if (!label || seen.has(label)) return;
      seen.add(label);
      options.push({ value: label, label });
    };
    paymentConditionOptions.forEach((o) => push(o.value || o.label));
    suppliers.forEach((supplier) => {
      (Array.isArray(supplier.payment_conditions) ? supplier.payment_conditions : []).forEach(push);
    });
    orders.forEach((order) => push(order.payment_terms));
    return options;
  }, [paymentConditionOptions, suppliers, orders]);

  const openCreate = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormError('');
    setFormData({ ...EMPTY_FORM, order_date: todayIso(), items: [{ ...EMPTY_LINE }] });
    setShowForm(true);
  };

  const openDetail = async (row) => {
    const poId = Number(row?.purchase_order_id || row?.id || 0);
    if (!poId) return;
    setDetailLoading(true);
    try {
      const full = await purchaseOrdersAPI.getById(poId);
      setDetailOrder(full);
    } catch (err) {
      window.alert(err?.message || 'No se pudo cargar la orden.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = async (order) => {
    const source = order?.items ? order : await purchaseOrdersAPI.getById(order.id);
    setDetailOrder(null);
    setIsEditing(true);
    setEditingId(source.id);
    setFormError('');
    setFormData(mapSourceToForm(source));
    setShowForm(true);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'supplier_id') {
        next.supplier_contact_id = '';
        const supplier = suppliers.find((s) => String(s.id) === String(value));
        if (supplier && Array.isArray(supplier.payment_conditions) && supplier.payment_conditions.length) {
          next.payment_terms = supplier.payment_conditions[0];
        } else {
          next.payment_terms = '';
        }
      }
      if (key === 'currency' && String(value).toUpperCase() === 'CLP') {
        next.exchange_rate = 1;
      }
      return next;
    });
  };

  const openQuickSupplierForm = () => {
    setQuickSupplierFormError('');
    setQuickSupplierFormData({ ...EMPTY_SUPPLIER_FORM });
    setShowQuickSupplierForm(true);
  };

  const handleQuickSupplierFormChange = (key, value) => {
    setQuickSupplierFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveQuickSupplier = async () => {
    const name = capitalizeWords(String(quickSupplierFormData.name || '').trim());
    if (!name) {
      setQuickSupplierFormError('El nombre es obligatorio.');
      return;
    }

    const addresses = (Array.isArray(quickSupplierFormData.addresses)
      ? quickSupplierFormData.addresses
      : []
    )
      .map((row) => ({
        address_type: String(row.address_type || 'Facturación').trim() || 'Facturación',
        address: normalizeProperCaseText(String(row.address || '').trim()),
        commune: capitalizeWords(String(row.commune || '').trim()),
        city: capitalizeWords(String(row.city || '').trim()),
        region: capitalizeWords(String(row.region || '').trim()),
        country: capitalizeWords(String(row.country || '').trim()) || 'Chile',
        notes: String(row.notes || '').trim(),
      }))
      .filter((row) => row.address);

    const bankAccounts = (Array.isArray(quickSupplierFormData.bank_accounts)
      ? quickSupplierFormData.bank_accounts
      : []
    )
      .map((row) => ({
        bank_name: capitalizeWords(String(row.bank_name || '').trim()),
        account_type: String(row.account_type || 'Corriente').trim() || 'Corriente',
        account_number: String(row.account_number || '').trim(),
        account_holder: capitalizeWords(String(row.account_holder || '').trim()),
        holder_rut: formatChileanRut(String(row.holder_rut || '').trim()),
        currency: String(row.currency || 'CLP').trim().toUpperCase() || 'CLP',
        status: String(row.status || 'Activo').trim() || 'Activo',
        notes: String(row.notes || '').trim(),
      }))
      .filter((row) => row.bank_name || row.account_number);

    const paymentConditions = Array.isArray(quickSupplierFormData.payment_conditions)
      ? quickSupplierFormData.payment_conditions
      : [];

    const payload = {
      name,
      business_name: capitalizeWords(String(quickSupplierFormData.business_name || '').trim()),
      rut: formatChileanRut(String(quickSupplierFormData.rut || '').trim()),
      address: addresses[0]?.address || '',
      supplier_type: String(quickSupplierFormData.supplier_type || '').trim(),
      status: String(quickSupplierFormData.status || 'Activo').trim() || 'Activo',
      payment_conditions: paymentConditions,
      addresses,
      bank_accounts: bankAccounts,
    };

    setIsSavingQuickSupplier(true);
    setQuickSupplierFormError('');
    try {
      const created = await suppliersAPI.create(payload);
      await syncSupplierContacts(created.id, quickSupplierFormData.contacts || []);
      await loadLookups();
      setFormData((prev) => ({
        ...prev,
        supplier_id: created?.id ? String(created.id) : prev.supplier_id,
        supplier_contact_id: '',
        payment_terms: paymentConditions[0] || '',
      }));
      setShowQuickSupplierForm(false);
      setQuickSupplierFormData({ ...EMPTY_SUPPLIER_FORM });
    } catch (error) {
      setQuickSupplierFormError(error?.message || 'No se pudo crear el proveedor.');
    } finally {
      setIsSavingQuickSupplier(false);
    }
  };

  const handleCreateCatalogItem = async (payload) => {
    const created = await purchaseOrdersAPI.createCatalogItem(payload);
    await loadLookups();
    return created;
  };

  const buildPayload = (statusOverride) => {
    const supplierId = Number(formData.supplier_id || 0);
    if (!supplierId) {
      setFormError('Debes seleccionar un proveedor.');
      return null;
    }
    const items = (formData.items || [])
      .map((item) => ({
        id: item.id || null,
        item_type: String(item.item_type || '').trim(),
        item_code: String(item.item_code || '').trim(),
        description: String(item.description || '').trim(),
        detail: String(item.detail || '').trim(),
        quantity: Number(item.quantity || 0),
        unit: String(item.unit || 'UN').trim() || 'UN',
        format_code: String(item.format_code || '').trim(),
        unit_price: Number(item.unit_price || 0),
        purchase_request_id: Number(item.purchase_request_id || 0) || null,
      }))
      .filter((item) => item.description || item.item_code);
    if (!items.length) {
      setFormError('Agrega al menos un ítem con descripción.');
      return null;
    }

    return {
      supplier_id: supplierId,
      supplier_contact_id: Number(formData.supplier_contact_id || 0) || null,
      po_code: String(formData.po_code || '').trim() || null,
      order_date: formData.order_date || todayIso(),
      expected_delivery_date: formData.expected_delivery_date || null,
      payment_terms: String(formData.payment_terms || '').trim(),
      currency: String(formData.currency || 'CLP').trim() || 'CLP',
      document_type: String(formData.document_type || '').trim(),
      exchange_rate: Number(formData.exchange_rate || 1) || 1,
      status: String(statusOverride || formData.status || 'Borrador').trim() || 'Borrador',
      delivery_place: String(formData.delivery_place || '').trim(),
      delivery_conditions: String(formData.delivery_conditions || '').trim(),
      buyer_name: String(formData.buyer_name || '').trim(),
      comments: String(formData.comments || '').trim(),
      notes: String(formData.notes || '').trim(),
      advance_amount: Number(formData.advance_amount || 0),
      withholdings_amount: Number(formData.withholdings_amount || 0),
      items,
    };
  };

  const persist = async (statusOverride) => {
    const payload = buildPayload(statusOverride);
    if (!payload) return null;

    setIsSaving(true);
    setFormError('');
    try {
      const saved =
        isEditing && editingId
          ? await purchaseOrdersAPI.update(editingId, payload)
          : await purchaseOrdersAPI.create(payload);
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      await Promise.all([loadOrders(), loadOrderItems(), loadLookups()]);
      return saved || null;
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar la orden de compra.');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => persist(formData.status || 'Borrador');

  const handleBulkImportOrders = async (rows) => {
    setIsImportingOrders(true);
    setBulkImportResult(null);
    try {
      const data = await purchaseOrdersAPI.bulkImport(Array.isArray(rows) ? rows : []);
      setBulkImportResult(data);
      await Promise.all([loadOrders(), loadOrderItems(), loadLookups()]);
      return data;
    } catch (error) {
      const fail = {
        created: 0,
        skipped: 0,
        errors: 1,
        error_rows: [{ row: 0, detail: error.message || 'No se pudo importar.' }],
      };
      setBulkImportResult(fail);
      throw error;
    } finally {
      setIsImportingOrders(false);
    }
  };

  const handleBulkImportItems = async (rows) => {
    setIsImportingItems(true);
    setItemsBulkImportResult(null);
    try {
      const data = await purchaseOrdersAPI.bulkImportItems(Array.isArray(rows) ? rows : []);
      setItemsBulkImportResult(data);
      await Promise.all([loadOrders(), loadOrderItems(), loadLookups()]);
      return data;
    } catch (error) {
      const fail = {
        created: 0,
        skipped: 0,
        errors: 1,
        orders_updated: 0,
        error_rows: [{ row: 0, detail: error.message || 'No se pudo importar.' }],
      };
      setItemsBulkImportResult(fail);
      throw error;
    } finally {
      setIsImportingItems(false);
    }
  };

  const handleEmit = async () => {
    const snapshot = { ...formData };
    const supplier =
      suppliers.find((s) => String(s.id) === String(snapshot.supplier_id)) || null;
    const contact =
      contacts.find((c) => String(c.id) === String(snapshot.supplier_contact_id)) || null;

    const saved = await persist('Emitida');
    if (!saved) return;

    try {
      await downloadPurchaseOrderPdf({
        po_code: saved.po_code || snapshot.po_code,
        order_date: saved.order_date || snapshot.order_date,
        currency: saved.currency || snapshot.currency || 'CLP',
        exchange_rate: saved.exchange_rate ?? snapshot.exchange_rate ?? 1,
        document_type: saved.document_type || snapshot.document_type || '',
        payment_terms: saved.payment_terms || snapshot.payment_terms || '',
        delivery_conditions: saved.delivery_conditions || snapshot.delivery_conditions || '',
        delivery_place: saved.delivery_place || snapshot.delivery_place || '',
        buyer_name: saved.buyer_name || snapshot.buyer_name || '',
        authorized_by: saved.buyer_name || snapshot.buyer_name || '',
        comments: saved.comments || snapshot.comments || '',
        advance_amount: saved.advance_amount ?? snapshot.advance_amount ?? 0,
        withholdings_amount: saved.withholdings_amount ?? snapshot.withholdings_amount ?? 0,
        supplier_name: supplier?.name || supplier?.business_name || '',
        supplier_rut: supplier?.rut || '',
        supplier_address: supplier?.address || '',
        vendor_company: supplier?.business_name || supplier?.name || '',
        vendor_rut: supplier?.rut || '',
        vendor_address: supplier?.address || '',
        vendor_contact_name: contact?.full_name || '',
        vendor_phone: contact?.phone || '',
        items: Array.isArray(saved.items) && saved.items.length ? saved.items : snapshot.items,
      });
    } catch (err) {
      window.alert(
        err?.message ||
          'La orden se emitió, pero no se pudo generar el PDF. Puedes reintentar desde la OC guardada.'
      );
    }
  };

  const handleBulkDelete = async (ids) => {
    if (!ids?.length) return false;
    const ok = window.confirm(
      ids.length === 1 ? '¿Eliminar esta orden de compra?' : `¿Eliminar ${ids.length} órdenes de compra?`
    );
    if (!ok) return false;
    await purchaseOrdersAPI.bulkDelete(ids);
    await Promise.all([loadOrders(), loadOrderItems()]);
    return true;
  };

  const handleBulkAssign = async (payload) => {
    const ids = Array.isArray(payload?.ids)
      ? payload.ids.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (!ids.length) throw new Error('No hay órdenes seleccionadas.');
    const body = { ids };
    if (payload.buyer_name) body.buyer_name = String(payload.buyer_name).trim();
    if (payload.status) body.status = String(payload.status).trim();
    if (Object.prototype.hasOwnProperty.call(payload, 'payment_terms')) {
      body.payment_terms = String(payload.payment_terms ?? '').trim();
    }
    await purchaseOrdersAPI.bulkAssign(body);
    await Promise.all([loadOrders(), loadOrderItems()]);
  };

  const handleDuplicateOrders = async (ids) => {
    const poIds = Array.isArray(ids)
      ? ids.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (!poIds.length) throw new Error('No hay órdenes seleccionadas.');
    const results = await Promise.allSettled(poIds.map((id) => purchaseOrdersAPI.duplicate(id)));
    const failed = results.find((r) => r.status === 'rejected');
    if (failed) throw new Error(failed.reason?.message || 'No se pudieron duplicar todas las órdenes.');
    await Promise.all([loadOrders(), loadOrderItems()]);
  };

  const handleNewVersionOrders = async (ids) => {
    const poIds = Array.isArray(ids)
      ? ids.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (!poIds.length) throw new Error('No hay órdenes seleccionadas.');
    const results = await Promise.allSettled(poIds.map((id) => purchaseOrdersAPI.newVersion(id)));
    const failed = results.find((r) => r.status === 'rejected');
    if (failed) throw new Error(failed.reason?.message || 'No se pudieron crear todas las versiones.');
    await Promise.all([loadOrders(), loadOrderItems()]);
  };

  const handleFragmentOrders = async (ids) => {
    const poIds = Array.isArray(ids)
      ? ids.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (!poIds.length) throw new Error('No hay órdenes seleccionadas.');
    const results = await Promise.allSettled(poIds.map((id) => purchaseOrdersAPI.fragment(id)));
    const failed = results.find((r) => r.status === 'rejected');
    if (failed) throw new Error(failed.reason?.message || 'No se pudieron fragmentar todas las órdenes.');
    await Promise.all([loadOrders(), loadOrderItems()]);
  };

  const handleRequestApproval = async (row) => {
    const poId = Number(row?.id || 0);
    if (!poId) return;
    const ok = window.confirm(
      `¿Solicitar aprobación para la orden ${row.po_code || poId}?`
    );
    if (!ok) return;
    try {
      await purchaseOrdersAPI.requestApproval(poId);
      await Promise.all([loadOrders(), loadOrderItems()]);
    } catch (err) {
      window.alert(err?.message || 'No se pudo solicitar la aprobación.');
    }
  };

  const openInvoicing = async (row) => {
    const poId = Number(row?.id || 0);
    if (!poId) return;
    setDetailLoading(true);
    try {
      const full = await purchaseOrdersAPI.getById(poId);
      setInvoiceOrder(full);
    } catch (err) {
      window.alert(err?.message || 'No se pudo cargar la orden para facturación.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveInvoicing = async (payload) => {
    const poId = Number(invoiceOrder?.id || 0);
    if (!poId) return;
    setInvoiceSaving(true);
    try {
      await purchaseOrdersAPI.updateInvoicing(poId, payload);
      setInvoiceOrder(null);
      await Promise.all([loadOrders(), loadOrderItems()]);
    } finally {
      setInvoiceSaving(false);
    }
  };

  const pendingApprovalCount = useMemo(
    () => orders.filter((order) => isPendingPoApprovalStatus(order.status)).length,
    [orders]
  );

  const ordersAmountClp = useMemo(
    () => orders.reduce((acc, order) => acc + poAmountClp(order), 0),
    [orders]
  );

  const listTabs = [
    { key: 'orders', label: 'Lista de Órdenes', count: pendingApprovalCount },
    { key: 'items', label: 'Ítems', count: orderItems.length },
  ];

  const listSubtitle = loading
    ? 'Cargando…'
    : error || `${orders.length} órdenes · ${formatSummaryMoney(ordersAmountClp)}`;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: '12px 18px',
          alignItems: 'start',
          marginBottom: 10,
        }}
      >
        <div>
          <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
            Órdenes de compra
          </Heading>
          <p style={{ margin: 0, maxWidth: 680, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
            Emite y da seguimiento a compras a proveedores. Las OC aprobadas, enviadas, recibidas o pagadas
            actualizan el acumulado del proveedor.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'nowrap',
            marginTop: '22px',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' ||
          periodFilter === 'annual_ytd' ||
          periodFilter === 'quarterly' ||
          periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: '10px',
          overflow: chartsCollapsed ? 'hidden' : 'visible',
          margin: '10px 0 12px',
          background: ui.panel,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '7px 10px',
            borderBottom: chartsCollapsed ? 'none' : `1px solid ${ui.border}`,
            background: ui.header,
            borderRadius: chartsCollapsed ? '10px' : '10px 10px 0 0',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '13px', color: ui.text, fontWeight: 700, lineHeight: 1.2 }}>
            Gráficos
          </h3>
          <button
            type="button"
            onClick={() => setChartsCollapsed((prev) => !prev)}
            aria-expanded={!chartsCollapsed}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: `1px solid ${ui.border}`,
              borderRadius: '6px',
              background: ui.panel,
              color: ui.text,
              fontSize: '11px',
              fontWeight: 600,
              padding: '3px 8px',
              cursor: 'pointer',
              lineHeight: 1.2,
              height: '28px',
            }}
          >
            {chartsCollapsed ? 'Mostrar' : 'Ocultar'}
            {chartsCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
        {!chartsCollapsed ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '12px',
              alignItems: 'start',
              overflow: 'visible',
              padding: '0 10px 4px',
            }}
          >
            <MonthlyDistributionChart
              periods={chartPeriods}
              items={statusChartItems}
              title="Distribución mensual por estado"
              totalLabel="órdenes"
              emptyMessage="Sin órdenes de compra para el período."
              stackDimensions={[PO_STATUS_STACK_DIMENSION]}
              defaultStackKey="status"
              valueMetrics={PO_STATUS_CHART_METRICS}
              defaultMetricKey="count"
              axisModes={PO_CHART_AXIS_MODES}
              defaultAxisKey="month"
            />
            <MonthlyDistributionChart
              periods={chartPeriods}
              items={itemTypeChartItems}
              title="Distribución mensual por tipo de compra"
              totalLabel="CLP"
              emptyMessage="Sin ítems de compra para el período."
              stackDimensions={[PO_ITEM_TYPE_STACK_DIMENSION]}
              defaultStackKey="itemType"
              valueMetrics={PO_ITEM_TYPE_CHART_METRICS}
              defaultMetricKey="amount"
              axisModes={PO_CHART_AXIS_MODES}
              defaultAxisKey="month"
            />
          </div>
        ) : null}
      </div>

      <PeriodSummaryTable
        periods={selectedPeriod.columns}
        rows={purchaseOrderSummaryRows}
        title={selectedPeriod.title || 'Resumen de órdenes de compra'}
        collapsed={summaryCollapsed}
        onToggleCollapsed={() => setSummaryCollapsed((prev) => !prev)}
        metricOptions={PO_SUMMARY_METRIC_OPTIONS}
        metric={summaryMetric}
        onMetricChange={setSummaryMetric}
      />

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Órdenes de compra</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => {
                setBulkImportResult(null);
                setItemsBulkImportResult(null);
                setShowBulkImportChooser(true);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 12px',
                borderRadius: 8,
                border: `1px solid ${ui.border}`,
                background: 'transparent',
                color: ui.text,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Upload size={16} /> Carga masiva
            </button>
            <button
              type="button"
              onClick={openCreate}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Plus size={16} /> Nueva OC
            </button>
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Vistas de órdenes de compra"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {listTabs.map((item) => {
            const active = listTab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setListTab(item.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        {listTab === 'items' ? (
          <PurchaseOrderItemsTable
            rows={orderItems}
            loading={itemsLoading || detailLoading}
            error={itemsError}
            isDarkMode={isDarkMode}
            hideChrome
            onPoClick={openDetail}
          />
        ) : (
          <PurchaseOrdersTable
            rows={orders}
            loading={loading || detailLoading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            onRowClick={openDetail}
            onBulkDelete={handleBulkDelete}
            onBulkAssign={handleBulkAssign}
            onDuplicate={handleDuplicateOrders}
            onNewVersion={handleNewVersionOrders}
            onFragment={handleFragmentOrders}
            onRequestApproval={handleRequestApproval}
            onInvoice={openInvoicing}
            buyerOptions={buyerOptions}
            paymentTermOptions={assignPaymentTermOptions}
          />
        )}
      </div>

      <PurchaseOrdersForm
        isOpen={showForm}
        onClose={() => !isSaving && setShowForm(false)}
        onSave={handleSave}
        onEmit={handleEmit}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        isEditing={isEditing}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        supplierOptions={supplierOptions}
        contactOptions={contactOptions}
        paymentTermOptions={paymentTermOptions}
        documentTypeOptions={documentTypeOptions}
        currencyOptions={currencyOptions}
        itemTypeOptions={itemTypeOptions}
        deliveryConditionOptions={deliveryConditionOptions}
        buyerOptions={buyerOptions}
        purchaseRequestOptions={purchaseRequestOptions}
        catalogProducts={catalogProducts}
        formatOptions={formatOptions}
        supplierRut={supplierRut}
        onCreateCatalogItem={handleCreateCatalogItem}
        onAddSupplier={openQuickSupplierForm}
      />

      <SuppliersForm
        isOpen={showQuickSupplierForm}
        onClose={() => !isSavingQuickSupplier && setShowQuickSupplierForm(false)}
        onSave={handleSaveQuickSupplier}
        isSaving={isSavingQuickSupplier}
        isDarkMode={isDarkMode}
        isEditing={false}
        formData={quickSupplierFormData}
        onChange={handleQuickSupplierFormChange}
        formError={quickSupplierFormError}
        paymentConditionOptions={paymentConditionOptions}
        supplierTypeOptions={supplierTypeOptions}
        currencyOptions={currencyOptions}
        zIndex={11000}
      />

      <PurchaseOrderDetailModal
        isOpen={Boolean(detailOrder)}
        onClose={() => setDetailOrder(null)}
        purchaseOrder={detailOrder}
        isDarkMode={isDarkMode}
        onEdit={openEdit}
      />

      <PurchaseOrderInvoicingModal
        isOpen={Boolean(invoiceOrder)}
        onClose={() => !invoiceSaving && setInvoiceOrder(null)}
        purchaseOrder={invoiceOrder}
        isDarkMode={isDarkMode}
        isSaving={invoiceSaving}
        onSave={handleSaveInvoicing}
      />

      {showBulkImportChooser ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 11000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setShowBulkImportChooser(false)}
        >
          <div
            style={{
              width: 'min(480px, 100%)',
              background: ui.page,
              borderRadius: 12,
              border: `1px solid ${ui.border}`,
              padding: 16,
              display: 'grid',
              gap: 14,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, color: ui.text, fontSize: 18 }}>Carga masiva</h3>
                <p style={{ margin: '4px 0 0', color: ui.muted, fontSize: 13 }}>
                  Elige si quieres importar un listado de órdenes o los ítems de OC ya creadas.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBulkImportChooser(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: ui.muted,
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Cerrar"
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${ui.border}`,
                  background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: ui.text }}>Listado de órdenes</div>
                <div style={{ marginTop: 2, fontSize: 12, color: ui.muted }}>
                  Cabeceras: proveedor, fecha, condiciones de pago y datos generales.
                </div>
              </div>
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${ui.border}`,
                  background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: ui.text }}>Ítems</div>
                <div style={{ marginTop: 2, fontSize: 12, color: ui.muted }}>
                  Líneas de producto para OC existentes (código, cantidad, unidad y precio).
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  setShowBulkImportChooser(false);
                  setBulkImportResult(null);
                  setShowBulkImportModal(true);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 38,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#2563eb',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <ListOrdered size={16} /> Listado de órdenes
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowBulkImportChooser(false);
                  setItemsBulkImportResult(null);
                  setShowItemsBulkImportModal(true);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 38,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#16a34a',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <Rows3 size={16} /> Ítems
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PurchaseOrdersBulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => {
          if (isImportingOrders) return;
          setShowBulkImportModal(false);
          setBulkImportResult(null);
        }}
        onImport={handleBulkImportOrders}
        isImporting={isImportingOrders}
        isDarkMode={isDarkMode}
        result={bulkImportResult}
        suppliers={suppliers}
        contacts={contacts}
        paymentTermOptions={assignPaymentTermOptions}
        documentTypeOptions={documentTypeOptions}
      />

      <PurchaseOrderItemsBulkImportModal
        isOpen={showItemsBulkImportModal}
        onClose={() => {
          if (isImportingItems) return;
          setShowItemsBulkImportModal(false);
          setItemsBulkImportResult(null);
        }}
        onImport={handleBulkImportItems}
        isImporting={isImportingItems}
        isDarkMode={isDarkMode}
        result={itemsBulkImportResult}
        orders={orders}
        catalogProducts={catalogProducts}
        itemTypeOptions={itemTypeOptions}
      />
    </div>
  );
};

export default PaintPurchaseOrdersWorkspace;
